import { IXM_AGENTAUTH_OAUTH_V1_AGENT_AUTH_SERVICE_APPROVE_AUTHORIZATION_REQUEST_AUDIENCE } from '@interxfi/api-client/generated/client-functions/ixm-agentauth-oauth-v1-agent-auth-service-approve-authorization-request';
import { IXM_AGENTAUTH_OAUTH_V1_AGENT_AUTH_SERVICE_INSPECT_AUTHORIZATION_REQUEST_AUDIENCE } from '@interxfi/api-client/generated/client-functions/ixm-agentauth-oauth-v1-agent-auth-service-inspect-authorization-request';
import type { IxmUserauthV1JoinedRealmAccess } from '@interxfi/types';

const AGENT_KEY_CONSENT_REQUEST_SESSION_STORAGE_KEY = 'agentKeyConsentRequest';

export interface ConsentRequest {
  request: string;
  iss: string;
}

export interface ConsentAccesses {
  keyAccess: IxmUserauthV1JoinedRealmAccess | undefined;
  tenantAccesses: IxmUserauthV1JoinedRealmAccess[];
}

function isConsentRequest(value: unknown): value is ConsentRequest {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const { request, iss } = value as Record<string, unknown>;

  return typeof request === 'string' && typeof iss === 'string' && Boolean(request) && Boolean(iss);
}

export function parseConsentFragment(hash: string): ConsentRequest | undefined {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const value = { request: params.get('request'), iss: params.get('iss') };

  return isConsentRequest(value) ? value : undefined;
}

export function readConsentRequest(): ConsentRequest | undefined {
  try {
    const stored = window.sessionStorage.getItem(AGENT_KEY_CONSENT_REQUEST_SESSION_STORAGE_KEY);
    if (!stored) {
      return undefined;
    }

    const parsed: unknown = JSON.parse(stored);

    return isConsentRequest(parsed) ? { request: parsed.request, iss: parsed.iss } : undefined;
  } catch {
    return undefined;
  }
}

export function writeConsentRequest(value: ConsentRequest): void {
  try {
    window.sessionStorage.setItem(AGENT_KEY_CONSENT_REQUEST_SESSION_STORAGE_KEY, JSON.stringify({ request: value.request, iss: value.iss }));
  } catch {}
}

export function clearConsentRequest(): void {
  try {
    window.sessionStorage.removeItem(AGENT_KEY_CONSENT_REQUEST_SESSION_STORAGE_KEY);
  } catch {}
}

function normalizeOrigin(value: string | undefined): string | undefined {
  if (!value) {
    return undefined;
  }

  try {
    const url = new URL(value);
    const path = url.pathname.replace(/\/+$/, '');

    return `${url.protocol}//${url.host}${path}`.toLowerCase();
  } catch {
    return undefined;
  }
}

function tenantKey(access: IxmUserauthV1JoinedRealmAccess): string {
  return `${access.tenant?.tenantType ?? ''}/${access.tenant?.tenantId ?? ''}`;
}

export function resolveConsentAccesses(accesses: IxmUserauthV1JoinedRealmAccess[] | undefined, iss: string): ConsentAccesses {
  const issuer = normalizeOrigin(iss);
  if (!issuer || !accesses?.length) {
    return { keyAccess: undefined, tenantAccesses: [] };
  }

  const matches = accesses.filter((access) => normalizeOrigin(access.realm?.data?.spec?.baseUrl) === issuer && access.tenant?.tenantId);
  const keyAccess = matches.find((access) =>
    IXM_AGENTAUTH_OAUTH_V1_AGENT_AUTH_SERVICE_INSPECT_AUTHORIZATION_REQUEST_AUDIENCE.includes(access.tenant?.tenantType ?? ''),
  );
  const seen = new Set<string>();
  const tenantAccesses = matches.filter((access) => {
    if (!IXM_AGENTAUTH_OAUTH_V1_AGENT_AUTH_SERVICE_APPROVE_AUTHORIZATION_REQUEST_AUDIENCE.includes(access.tenant?.tenantType ?? '')) {
      return false;
    }

    const key = tenantKey(access);
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });

  return { keyAccess, tenantAccesses };
}

export function getConsentTenantKey(access: IxmUserauthV1JoinedRealmAccess): string {
  return tenantKey(access);
}
